import React, { useState, useRef } from 'react';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Click,
  AreaMap,
  Chair,
  Round,
  DoorHandle,
  Layers,
  EveryUser,
  GraphicStitching,
  Ruler,
  Text,
  Square,
} from '@icon-park/react';
import { ChevronRight } from 'lucide-react';
import { useEditorStore } from '../store/useEditorStore';
import { ToolType } from '../types';
import FurnitureLibrary from './FurnitureLibrary';
import { useTranslation } from 'react-i18next';

interface ToolButton {
  type: ToolType;
  icon: React.ReactNode;
  labelKey: string;
  descriptionKey: string;
  shortcut?: string;
}



// 绘制工具子菜单
const drawingTools: ToolButton[] = [
  { 
    type: ToolType.RECTANGLE, 
    icon: <Square size={18} theme="outline" />, 
    labelKey: 'toolbar.rectTool', 
    descriptionKey: 'toolbar.rectTool',
    shortcut: 'R' 
  },
  { 
    type: ToolType.CIRCLE, 
    icon: <Round size={18} theme="outline" />, 
    labelKey: 'toolbar.circleTool', 
    descriptionKey: 'toolbar.circleTool',
    shortcut: 'C' 
  },
  { 
    type: ToolType.POLYGON, 
    icon: <GraphicStitching size={18} theme="outline" />, 
    labelKey: 'toolbar.polygonTool', 
    descriptionKey: 'toolbar.polygonTool',
    shortcut: 'P' 
  },
];


// 主工具列表（不包含绘制工具组和家具工具）
const mainTools: ToolButton[] = [
  { 
    type: ToolType.SELECT, 
    icon: <Click size={18} theme="outline" />, 
    labelKey: 'toolbar.selectTool', 
    descriptionKey: 'toolbar.selectDesc',
    shortcut: 'V' 
  },
  { 
    type: ToolType.WALL, 
    icon: <Layers size={18} theme="outline" />, 
    labelKey: 'toolbar.wallTool', 
    descriptionKey: 'toolbar.wallDesc',
    shortcut: 'W' 
  },
  { 
    type: ToolType.AREA, 
    icon: <AreaMap size={18} theme="outline" />, 
    labelKey: 'toolbar.areaTool', 
    descriptionKey: 'toolbar.areaDesc',
    shortcut: 'A' 
  },
  { 
    type: ToolType.DOOR, 
    icon: <DoorHandle size={18} theme="outline" />, 
    labelKey: 'toolbar.doorTool', 
    descriptionKey: 'toolbar.doorDesc',
    shortcut: 'D' 
  },
  { 
    type: ToolType.STAIRS, 
    icon: <EveryUser size={18} theme="outline" />, 
    labelKey: 'toolbar.stairTool', 
    descriptionKey: 'toolbar.stairDesc',
    shortcut: 'S' 
  },
  { 
    type: ToolType.MEASURE, 
    icon: <Ruler size={18} theme="outline" />, 
    labelKey: 'toolbar.measureTool', 
    descriptionKey: 'toolbar.measureDesc',
    shortcut: 'Q' 
  },
  { 
    type: ToolType.TEXT, 
    icon: <Text size={18} theme="outline" />, 
    labelKey: 'toolbar.textTool', 
    descriptionKey: 'toolbar.textDesc',
    shortcut: 'T' 
  },
];


const Toolbar: React.FC = () => {
  const { t } = useTranslation();
  const { currentTool, setCurrentTool, project, setGuidedTourEnabled } = useEditorStore();
  const hasBaseMap = !!project?.mapConfig?.baseMap;
  const hasValidArea = !!project?.mapConfig?.validArea && project.mapConfig.validArea.length >= 3;

  const [showDrawingMenu, setShowDrawingMenu] = useState(false);
  const [showFurnitureLibrary, setShowFurnitureLibrary] = useState(false);
  const drawingMenuRef = useRef<HTMLDivElement>(null);
  const furnitureMenuRef = useRef<HTMLDivElement>(null);

  // 检查工具是否禁用
  const isToolDisabled = (toolType: ToolType) => {
    const needsValidArea = toolType !== ToolType.SELECT && toolType !== ToolType.AREA;
    if (!hasBaseMap && toolType !== ToolType.SELECT) return true;
    if (hasBaseMap && needsValidArea && !hasValidArea) return true;
    return false;
  };

  // 获取禁用提示
  const getWarningMessage = (toolType: ToolType) => {
    if (!hasBaseMap && toolType !== ToolType.SELECT) {
      return t('toolbar.needCAD');
    }
    const needsValidArea = toolType !== ToolType.SELECT && toolType !== ToolType.AREA;
    if (hasBaseMap && needsValidArea && !hasValidArea) {
      return t('toolbar.drawAreaFirst');
    }
    return '';
  };

  // 检查绘制工具组是否有选中的工具
  const isDrawingToolActive = drawingTools.some(t => t.type === currentTool);
  const drawingGroupDisabled = !hasBaseMap || !hasValidArea;

  // 渲染单个工具按钮
  const renderToolButton = (tool: ToolButton) => {
    const isDisabled = isToolDisabled(tool.type);
    const warningMessage = getWarningMessage(tool.type);

    return (
      <Tooltip
        key={tool.type}
        placement="right"
        getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement || document.body}
        title={
          <div className="text-sm">
            <div className="font-semibold mb-1">{t(tool.labelKey)}</div>
            <div className="text-gray-300 mb-1">{t(tool.descriptionKey)}</div>
            {tool.shortcut && (
              <div className="text-xs text-gray-400">{t('toolbar.shortcut', { key: tool.shortcut })}</div>
            )}
            {warningMessage && (
              <div className="text-xs text-amber-400 mt-1">{warningMessage}</div>
            )}
          </div>
        }
      >
        <button
          onClick={() => setCurrentTool(tool.type)}
          disabled={isDisabled}
          data-tool={tool.type}
          className={`
            w-10 h-10 rounded-sm flex items-center justify-center transition-all
            ${currentTool === tool.type
              ? 'bg-primary-500 text-white shadow'
              : isDisabled
              ? 'text-gray-300 cursor-not-allowed opacity-50'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }
          `}
        >
          {tool.icon}
        </button>
      </Tooltip>
    );
  };


  return (
    <div className="w-12 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-1">
      {/* 主工具：选择、墙面、区域 */}
      {mainTools.slice(0, 3).map(renderToolButton)}

      {/* 绘制工具组 */}
      <div 
        ref={drawingMenuRef}
        className="relative"
        onMouseEnter={() => setShowDrawingMenu(true)}
        onMouseLeave={() => setShowDrawingMenu(false)}
      >
        <Tooltip
          placement="right"
          open={showDrawingMenu ? false : undefined}
          mouseEnterDelay={0.5}
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement || document.body}
          overlayClassName="toolbar-tooltip"
          title={
            <div className="text-sm">
              <div className="font-semibold mb-1">{t('toolbar.drawTools')}</div>
              <div className="text-gray-300 mb-1">{t('toolbar.drawDesc')}</div>
              {drawingGroupDisabled && (
                <div className="text-xs text-amber-400 mt-1">
                  {!hasBaseMap ? t('toolbar.needCAD') : t('toolbar.drawAreaFirst')}
                </div>
              )}
            </div>
          }
        >
          <button
            disabled={drawingGroupDisabled}
            className={`
              w-10 h-10 rounded-sm flex items-center justify-center transition-all relative
              ${isDrawingToolActive
                ? 'bg-primary-500 text-white shadow'
                : drawingGroupDisabled
                ? 'text-gray-300 cursor-not-allowed opacity-50'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }
            `}
          >
            <Square size={18} theme="outline" />
            <ChevronRight size={10} className="absolute right-1 bottom-1" />
          </button>
        </Tooltip>

        {/* 绘制工具子菜单 */}
        {showDrawingMenu && !drawingGroupDisabled && (
          <>
            {/* 连接区域，覆盖按钮到子菜单的整个路径，防止鼠标移动时菜单消失 */}
            <div className="absolute left-full top-0 w-6 h-10 -mt-0" />
            <div className="absolute left-full top-0 ml-1 bg-white rounded-sm shadow-lg border border-gray-200 py-2 z-[1000] min-w-[140px]">
              <div className="px-3 py-1 text-xs text-gray-500 font-medium border-b border-gray-100 mb-1 whitespace-nowrap">
                {t('toolbar.drawTools')}
              </div>
              {drawingTools.map((tool) => (
                <button
                  key={tool.type}
                  onClick={() => {
                    setCurrentTool(tool.type);
                    setShowDrawingMenu(false);
                  }}
                  className={`
                    w-full px-3 py-2 flex items-center gap-2 text-sm transition-colors whitespace-nowrap
                    ${currentTool === tool.type
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {tool.icon}
                  <span>{t(tool.labelKey)}</span>
                  {tool.shortcut && (
                    <span className="ml-auto text-xs text-gray-400">{tool.shortcut}</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>


      {/* 家具工具 - hover展开3D建模仓库 */}
      <div
        ref={furnitureMenuRef}
        className="relative"
        onMouseEnter={() => setShowFurnitureLibrary(true)}
        onMouseLeave={() => setShowFurnitureLibrary(false)}
      >
        <Tooltip
          placement="right"
          open={showFurnitureLibrary ? false : undefined}
          mouseEnterDelay={0.5}
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement || document.body}
          overlayClassName="toolbar-tooltip"
          title={
            <div className="text-sm">
              <div className="font-semibold mb-1">{t('toolbar.furnitureTool')}</div>
              <div className="text-gray-300 mb-1">{t('toolbar.furnitureDesc')}</div>
              {drawingGroupDisabled && (
                <div className="text-xs text-amber-400 mt-1">
                  {!hasBaseMap ? t('toolbar.needCAD') : t('toolbar.drawAreaFirst')}
                </div>
              )}
            </div>
          }
        >
          <button
            disabled={drawingGroupDisabled}
            data-tool="furniture"
            className={`
              w-10 h-10 rounded-sm flex items-center justify-center transition-all relative
              ${drawingGroupDisabled
                ? 'text-gray-300 cursor-not-allowed opacity-50'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }
            `}
          >
            <Chair size={18} theme="outline" />
            <ChevronRight size={10} className="absolute right-1 bottom-1" />
          </button>
        </Tooltip>

        {/* 3D建模仓库面板 */}
        {showFurnitureLibrary && !drawingGroupDisabled && (
          <>
            {/* 连接区域，防止鼠标移动时菜单消失 - 覆盖整个按钮高度 */}
            <div className="absolute left-full top-0 w-6 h-10" />
            <FurnitureLibrary onClose={() => setShowFurnitureLibrary(false)} />
          </>
        )}
      </div>

      {/* 其他工具：门、楼梯、测量、文本、尺寸标注 */}
      {mainTools.slice(3).map(renderToolButton)}

      {/* 引导按钮 - 底部对齐，始终显示 */}
      <div className="mt-auto pt-2 border-t border-gray-100">
        <Tooltip placement="right" title={t('tour.title', '新手引导')}>
          <button
            onClick={() => setGuidedTourEnabled(true)}
            className="w-10 h-10 rounded-sm flex items-center justify-center text-gray-500 hover:text-primary-500 hover:bg-gray-50 transition-all"
          >
            <QuestionCircleOutlined style={{ fontSize: '18px' }} />
          </button>
        </Tooltip>
      </div>

    </div>
  );
};

export default Toolbar;
